import React, { useState } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import axios from "axios";

import {
  useBillingPlan,
  useBillingForecast,
  usePaymentMethods,
  useOrgBilling,
} from "src/domains/application/hooks/useBilling";
import { ApplicationEndpoints } from "src/generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EmptyState } from "src/shell/components/EmptyState";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import { ALPHA } from "src/shell/styles/designTokens";
import { getCardStyles, sectionLabelSx } from "src/shell/styles/themeUtils";

const OrgBillingTab: React.FC<{ orgAlias: string }> = ({ orgAlias }) => {
  const theme = useTheme();
  const orgBilling = useOrgBilling(orgAlias);
  const { data: plan, loading, error, refresh, isFallback } = useBillingPlan();
  const {
    data: forecast,
    loading: forecastLoading,
    error: forecastError,
  } = useBillingForecast();
  const {
    data: paymentMethods,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
  } = usePaymentMethods();
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activated, setActivated] = useState(false);

  const authHeader = () => ({
    withCredentials: true as const,
  });

  async function handleActivate() {
    setActivating(true);
    setActivateError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.ACTIVATE_BILLING}`,
        {},
        { ...authHeader() },
      );
      setActivated(true);
      refresh();
    } catch {
      setActivateError(
        "Failed to activate billing. Please try again or contact support.",
      );
    } finally {
      setActivating(false);
    }
  }

  if (loading) {
    return (
      <Stack spacing={3}>
        <Skeleton width={200} height={28} />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={80} />
        <Skeleton variant="rounded" height={120} />
      </Stack>
    );
  }

  if (error) {
    return (
      <ErrorMessage message="Failed to load billing information. Please try again." />
    );
  }

  if (!plan) return null;

  const cardSx = getCardStyles(theme);

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        {/* Billing Plan */}
        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Billing status
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    plan.plan === "paid"
                      ? alpha(theme.palette.success.main, ALPHA.STAT_TILE_BG)
                      : alpha(theme.palette.primary.main, ALPHA.STAT_TILE_BG),
                  color:
                    plan.plan === "paid"
                      ? theme.palette.success.main
                      : theme.palette.primary.main,
                  flexShrink: 0,
                }}
              >
                <CreditCardIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {plan.plan === "paid" ? "Paid (usage-based)" : "Free tier"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {plan.plan === "paid"
                    ? "Metered per CPU/RAM hour — billed monthly for actual usage."
                    : `Limited to ${plan.freeTierUsage?.stackLimit ?? "—"} stacks. Usage is metered but not charged.`}
                </Typography>
              </Box>
              {plan.plan === "paid" && (
                <CheckCircleOutlineIcon color="success" />
              )}
            </Stack>
            {plan.plan === "free" && !activated && !isFallback && (
              <Box sx={{ mt: 2 }}>
                {activateError && (
                  <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1 }}>
                    {activateError}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    activating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CreditCardIcon />
                    )
                  }
                  disabled={activating}
                  onClick={() => void handleActivate()}
                  sx={{ textTransform: "none" }}
                >
                  {activating ? "Activating..." : "Upgrade plan"}
                </Button>
              </Box>
            )}
            {activated && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon />}
                sx={{ mt: 2, borderRadius: 1 }}
              >
                Paid billing activated. Your plan has been updated.
              </Alert>
            )}
          </Card>
        </Grid>

        {/* Usage Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Usage summary
            </Typography>
            {forecastError ? (
              <ErrorMessage message={forecastError} />
            ) : (
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Current month
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {forecastLoading || !forecast
                      ? "—"
                      : `$${forecast.currentMonthUsd}`}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Forecast
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {forecastLoading || !forecast
                      ? "—"
                      : `$${forecast.forecastUsd}`}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Payment method
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {paymentMethodsError
                      ? "—"
                      : paymentMethodsLoading || !paymentMethods?.length
                        ? "None on file"
                        : `${paymentMethods[0].brand} •••• ${paymentMethods[0].last4}`}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Usage breakdown
            </Typography>
            {orgBilling.loading ? (
              <CircularProgress size={16} />
            ) : orgBilling.error || !orgBilling.data ? (
              <EmptyState
                icon={CreditCardIcon}
                title="Usage unavailable"
                subtitle="Usage details will appear after billing data is recorded."
              />
            ) : (
              <Stack spacing={1}>
                {orgBilling.data.systems
                  .flatMap((system) => system.lineItems)
                  .reduce<{ label: string; total: number }[]>((rows, item) => {
                    const current = rows.find(
                      (row) => row.label === item.label,
                    );
                    const amount = Number.parseFloat(
                      item.total.replace(/^\$/, ""),
                    );
                    if (!Number.isFinite(amount)) return rows;
                    if (current) current.total += amount;
                    else rows.push({ label: item.label, total: amount });
                    return rows;
                  }, [])
                  .map((row) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${row.total.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={cardSx}>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Invoices
            </Typography>
            <EmptyState
              icon={ReceiptLongIcon}
              title="Invoices unavailable"
              subtitle="Invoices will appear when the billing history service is available."
            />
          </Card>
        </Grid>
      </Grid>

      {/* Payment Methods */}
      <Card sx={cardSx}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Payment method
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<CreditCardIcon fontSize="small" />}
            sx={{ textTransform: "none" }}
          >
            Add method
          </Button>
        </Stack>
        {paymentMethodsError ? (
          <ErrorMessage message={paymentMethodsError} />
        ) : paymentMethodsLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={48} />
          </Stack>
        ) : !paymentMethods?.length ? (
          <EmptyState
            icon={CreditCardIcon}
            title="No payment methods"
            subtitle="Add a card to enable paid billing and remove stack limits."
            actionLabel="Add method"
          />
        ) : (
          <Stack spacing={0}>
            {paymentMethods.map((pm, index) => (
              <Stack
                key={pm.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 1.5,
                  px: 1.5,
                  borderBottom:
                    index < paymentMethods.length - 1
                      ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                      : "none",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                    }}
                  >
                    <CreditCardIcon fontSize="small" />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {pm.brand} •••• {pm.last4}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Expires {String(pm.expMonth).padStart(2, "0")}/{pm.expYear}
                  </Typography>
                  {pm.isDefault && (
                    <Chip
                      label="Default"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>
    </Stack>
  );
};

export default OrgBillingTab;
